"use client";

import * as React from "react";
import { MapPin, ShieldCheck, FileText, Leaf, Sprout, Coffee, Wheat, PackageSearch } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogTrigger, DialogContent, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
const catalogData = [{
  id: "p1",
  category: "rice",
  title: "Premium Basmati Rice",
  shortDesc: "Long-grain, aromatic rice sourced directly from select farms.",
  fullDesc: "Our Premium Basmati Rice is cultivated in the fertile plains, ensuring extra-long grains and a distinct, natural aroma. Perfect for biryanis and pilafs, it undergoes rigorous quality checks before export.",
  sourcing: "Hyderabad Agricultural Hub, India",
  certs: ["USDA Organic", "ISO 9001:2015", "Export Quality"],
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/catalog_basmati_rice_raw.png",
  macro: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/catalog_basmati_rice_macro.png",
  icon: Wheat
}, {
  id: "p2",
  category: "spices",
  title: "Authentic Guntur Mirchi",
  shortDesc: "Fiery red chilies known for their vibrant color and intense heat.",
  fullDesc: "Sourced directly from the Guntur region, these chilies are world-renowned for their pungency and rich red color. They are sun-dried and packed under strict hygienic conditions to retain their volatile oils.",
  sourcing: "Guntur Region via Hyderabad",
  certs: ["Spice Board of India", "Non-GMO Project Verified"],
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/catalog_guntur_mirchi_raw.png",
  macro: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/catalog_guntur_mirchi_macro.png",
  icon: Sprout
}, {
  id: "p3",
  category: "specialty",
  title: "Rich Cocoa Beans",
  shortDesc: "Premium fermented cocoa beans for high-end chocolate production.",
  fullDesc: "Carefully fermented and sun-dried, our cocoa beans offer a complex flavor profile with deep earthy notes. Ideal for artisanal chocolatiers seeking ethically sourced, high-quality raw materials.",
  sourcing: "Southern India Estates",
  certs: ["Fair Trade Certified", "Rainforest Alliance"],
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/catalog_cocoa_beans_raw.png",
  macro: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/catalog_cocoa_beans_macro.png",
  icon: Coffee
}, {
  id: "p4",
  category: "specialty",
  title: "Live Indoor Plants",
  shortDesc: "Carefully cultivated ornamental plants ready for global export.",
  fullDesc: "A diverse selection of robust, disease-free live plants cultivated in controlled environments. We handle all phytosanitary requirements to ensure safe and compliant international transit.",
  sourcing: "Cosmic EXIM Partner Nurseries",
  certs: ["Phytosanitary Certified", "Global GAP"],
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/catalog_live_plants_raw.png",
  macro: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/catalog_live_plants_macro.png",
  icon: Leaf
}, {
  id: "p5",
  category: "eco",
  title: "Biodegradable Cutlery",
  shortDesc: "Plant-based, eco-friendly plates and cutlery for sustainable dining.",
  fullDesc: "Manufactured from fallen leaves and plant fibers, our eco-friendly tableware is 100% compostable. A perfect sustainable alternative for events, catering, and commercial use.",
  sourcing: "Apex, NC & Partner Facilities",
  certs: ["BPI Certified Compostable", "FDA Approved"],
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/catalog_eco_cutlery_raw.png",
  macro: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/catalog_eco_cutlery_macro.png",
  icon: PackageSearch
}, {
  id: "p6",
  category: "spices",
  title: "Turmeric Fingers",
  shortDesc: "High-curcumin turmeric sourced from traditional growers.",
  fullDesc: "Our turmeric fingers boast a high curcumin content, offering vibrant color and potent health benefits. Polished and graded to meet international culinary and medicinal standards.",
  sourcing: "Hyderabad Spice Markets",
  certs: ["Organic Certified", "GMP Certified"],
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/catalog_turmeric_fingers_raw.png",
  macro: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/catalog_turmeric_fingers_macro.png",
  icon: Sprout
}];
const categories = [{
  id: "all",
  label: "All Products"
}, {
  id: "rice",
  label: "Premium Rice"
}, {
  id: "spices",
  label: "Regional Spices"
}, {
  id: "specialty",
  label: "Specialty Imports"
}, {
  id: "eco",
  label: "Eco-Friendly"
}];
export default function PremiumTradeCatalog() {
  return <section id="premium-trade-catalog" className="py-24 bg-background relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-secondary/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-sm font-bold tracking-wider uppercase text-secondary mb-3">
            Global Trade Portfolio
          </h2>
          <h3 className="text-3xl md:text-4xl lg:text-5xl font-serif font-semibold text-foreground mb-6">
            Premium Agricultural & Commercial Imports
          </h3>
          <p className="text-lg text-muted-foreground">
            Explore our curated catalog of high-quality goods, bridging the rich agricultural hubs of Hyderabad with the demanding markets of the USA.
          </p>
        </div>

        <Tabs defaultValue="all" className="w-full flex flex-col items-center">
          <TabsList className="mb-12 flex-wrap justify-center h-auto p-1 bg-muted/50 backdrop-blur-sm border border-border/50">
            {categories.map((cat, i) => <TabsTrigger key={cat.id} value={cat.id} data-index={i} className="px-6 py-2.5 text-sm md:text-base rounded-md data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm transition-all">
                {cat.label}
              </TabsTrigger>)}
          </TabsList>

          {categories.map((cat, i) => {
          const filteredData = cat.id === "all" ? catalogData : catalogData.filter(item => item.category === cat.id);
          return <TabsContent key={cat.id} value={cat.id} data-index={i} className="w-full outline-none focus-visible:ring-0">
                <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
                  {filteredData.map((product, index) => <div key={product.id} data-index={index} className="break-inside-avoid">
                      <Dialog>
                        <DialogTrigger asChild>
                          <div className="group relative overflow-hidden rounded-2xl cursor-pointer border border-border/50 bg-card shadow-sm transition-all duration-500 hover:shadow-xl hover:border-primary/30">
                            <div className="aspect-[4/5] w-full overflow-hidden bg-muted">
                              <img src={product.img} alt={product.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                            </div>
                            
                            {}
                            <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6 md:p-8">
                              <div className="translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                                <div className="flex items-center gap-2 mb-3">
                                  <product.icon className="size-5 text-secondary" />
                                  <span className="text-xs font-medium tracking-wider uppercase text-primary-foreground/80">
                                    {categories.find(c => c.id === product.category)?.label}
                                  </span>
                                </div>
                                <h4 className="text-primary-foreground font-serif text-xl md:text-2xl font-semibold mb-2">
                                  {product.title}
                                </h4>
                                <p className="text-primary-foreground/90 text-sm mb-4 line-clamp-2">
                                  {product.shortDesc}
                                </p>
                                <div className="flex items-center gap-2 text-primary-foreground/70 text-xs">
                                  <MapPin className="size-3.5" />
                                  <span>{product.sourcing}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </DialogTrigger>

                        <DialogContent className="max-w-4xl p-0 overflow-hidden border-none bg-transparent shadow-2xl sm:rounded-2xl">
                          <div className="grid grid-cols-1 md:grid-cols-5 bg-background h-full max-h-[90vh] overflow-y-auto md:overflow-hidden">
                            {}
                            <div className="relative h-64 md:h-auto md:col-span-2 bg-muted">
                              <img src={product.macro} alt={`${product.title} macro detail`} className="absolute inset-0 w-full h-full object-cover" />
                              <div className="absolute inset-0 bg-primary/10 mix-blend-multiply" />
                            </div>
                            
                            {}
                            <div className="p-6 md:p-10 flex flex-col justify-center bg-card md:col-span-3">
                              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary/10 text-secondary text-xs font-semibold tracking-wide uppercase w-fit mb-4">
                                <product.icon className="size-3.5" />
                                {categories.find(c => c.id === product.category)?.label}
                              </div>
                              
                              <DialogTitle className="text-2xl md:text-3xl lg:text-4xl font-serif text-foreground mb-4 leading-tight">
                                {product.title}
                              </DialogTitle>
                              
                              <DialogDescription className="text-base text-muted-foreground mb-8 leading-relaxed">
                                {product.fullDesc}
                              </DialogDescription>

                              <div className="space-y-6 mb-8 bg-background/50 p-6 rounded-xl border border-border/50">
                                <div className="flex items-start gap-4">
                                  <div className="bg-primary/10 p-2.5 rounded-lg text-primary shrink-0">
                                    <MapPin className="size-5" />
                                  </div>
                                  <div>
                                    <h5 className="font-semibold text-sm text-foreground mb-1">Sourcing Origin</h5>
                                    <p className="text-sm text-muted-foreground">{product.sourcing}</p>
                                  </div>
                                </div>
                                
                                <div className="w-full h-px bg-border/50" />
                                
                                <div className="flex items-start gap-4">
                                  <div className="bg-secondary/10 p-2.5 rounded-lg text-secondary shrink-0">
                                    <ShieldCheck className="size-5" />
                                  </div>
                                  <div>
                                    <h5 className="font-semibold text-sm text-foreground mb-2">Export Certifications</h5>
                                    <div className="flex flex-wrap gap-2">
                                      {product.certs.map((cert, idx) => <span key={idx} data-index={idx} className="text-xs bg-muted text-muted-foreground px-2.5 py-1 rounded-md border border-border/50">
                                          {cert}
                                        </span>)}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <DialogFooter className="sm:justify-start mt-auto">
                                <Button size="lg" className="w-full sm:w-auto gap-2 shadow-md">
                                  <FileText className="size-4" /> 
                                  Request Specifications & Quote
                                </Button>
                              </DialogFooter>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>)}
                </div>
              </TabsContent>;
        })}
        </Tabs>
      </div>
    </section>;
}